import React, { useEffect, useState } from 'react';
import {
  Stack, Typography, Box, Chip, Paper, Button, Divider,
  TextField, FormControlLabel, Switch, IconButton, Tooltip, Autocomplete,
} from '@mui/material';
import {
  Add, DeleteOutline, Calculate,
} from '@mui/icons-material';

import SectionCard from './SectionCard';
import HelpTip from '../../common/HelpTip';
import { TableCard, GroupLabel } from '../tabLayout';
import SearchableSelect, { SelectOption } from '../../common/SearchableSelect';
import EmptyState from '../../common/EmptyState';
import { ToneChip } from '../../common/StatusChip';
import { Fact, FactGrid, ListRow, QuietNote, numSx } from './sectionParts';
import { assignKeys } from '../../../utils/fieldKey';
import { AssessmentPathway, MeritComponent, MeritComponentSource, MeritTieBreaker, CustomCriterion } from '../../../types/admissions';

const FORMULA_HELP = {
  title: 'How the merit score is worked out',
  terms: [
    ['Weight', 'how much each part counts.'],
    ['Normalize', 'turns a part into a percentage of its maximum first, so a paper out of 50 and a round out of 20 count fairly.'],
    ['Weighted sum', 'each part times its weight, added up.'],
    ['Weighted average', 'the weighted sum divided by the total weight.'],
    ['Scale to 0 – 100', 'also divides by the total weight; with every part normalized, the score reads out of 100.'],
  ] as const,
};

const TIE_HELP = {
  title: 'Breaking ties',
  text: 'When two applicants have the same merit score, the first rule decides; if they are still level, the next one does.',
};

interface Props {
  pathway: AssessmentPathway;
  canEdit: boolean;
  /** Why the section can't change on this open cycle (marks / offers in). */
  lockedReason?: string | null;
  onSave: (patch: Partial<AssessmentPathway>) => Promise<void>;
}

/**
 * ScoringConfigSection — the pathway's merit formula and custom criteria.
 */

const SOURCE_LABEL: Record<MeritComponentSource, string> = {
  test: 'Test',
  interview: 'Interview',
  custom: 'Custom criterion',
  previousPercentage: 'Previous school %',
};

const TIE_BREAKER_LABEL: Record<MeritTieBreaker, string> = {
  test_score: 'Higher test score',
  interview_score: 'Higher interview score',
  previous_percentage: 'Higher previous %',
  dob_older: 'Older applicant',
  dob_younger: 'Younger applicant',
  application_date: 'Earlier application',
};

const ALL_TIE_BREAKERS: MeritTieBreaker[] = [
  'test_score', 'interview_score', 'previous_percentage',
  'dob_older', 'dob_younger', 'application_date',
];

const MODE_OPTIONS: SelectOption[] = [
  { value: 'weighted-sum', label: 'Weighted sum' },
  { value: 'weighted-average', label: 'Weighted average' },
];

const SOURCE_OPTIONS: SelectOption[] = (Object.keys(SOURCE_LABEL) as MeritComponentSource[])
  .map(src => ({ value: src, label: SOURCE_LABEL[src] }));


/** A scoring config with defaults filled in — the editor's starting point. */
const withDefaults = (config?: AssessmentPathway['scoringConfig']) => ({
  customCriteria: config?.customCriteria || [],
  meritFormula: {
    components: config?.meritFormula?.components || [],
    mode: (config?.meritFormula?.mode || 'weighted-sum') as 'weighted-sum' | 'weighted-average',
    rounding: config?.meritFormula?.rounding ?? 2,
    scaleTo100: config?.meritFormula?.scaleTo100 ?? true,
    tieBreakers: config?.meritFormula?.tieBreakers || [],
  },
});

const ScoringConfigSection: React.FC<Props> = ({ pathway, canEdit, lockedReason, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => withDefaults(pathway.scoringConfig));

  useEffect(() => {
    if (editing) setDraft(withDefaults(pathway.scoringConfig));
  }, [editing, pathway.scoringConfig]);

  const effective = editing ? draft : withDefaults(pathway.scoringConfig);

  // Saved criteria keep their keys (marks point at them); a criterion added
  // in this edit takes its key from its label as it is typed, so the merit
  // formula below can already pick it.
  const savedCriterionKeys = new Set((pathway.scoringConfig?.customCriteria || []).map(c => c.key));

  const empty = effective.meritFormula.components.length === 0 && effective.customCriteria.length === 0;
  const weightSum = effective.meritFormula.components.reduce((s, c) => s + (c.weight || 0), 0);

  // Interview rounds live on the cycle (edited separately in InterviewConfigSection).
  const interviewRoundOptions: SelectOption[] = (pathway.interviewConfig?.rounds || []).map(r => ({
    value: r.key, label: r.name,
  }));
  // Custom criteria are edited inline in THIS section, so draft takes precedence.
  const customCriterionOptions: SelectOption[] = effective.customCriteria.map(c => ({
    value: c.key, label: c.label || c.key,
  }));

  // Resolve a component.key back to a human label for the read-only chip.
  const resolveKeyLabel = (c: MeritComponent): string | undefined => {
    if (!c.key) return undefined;
    if (c.source === 'interview') {
      return interviewRoundOptions.find(o => o.value === c.key)?.label || c.key;
    }
    if (c.source === 'custom') {
      return customCriterionOptions.find(o => o.value === c.key)?.label || c.key;
    }
    return c.key;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        scoringConfig: {
          customCriteria: draft.customCriteria.filter(c => c.label.trim() && c.maxMarks > 0),
          meritFormula: {
            components: draft.meritFormula.components.filter(c => c.weight > 0),
            mode: draft.meritFormula.mode,
            rounding: draft.meritFormula.rounding,
            scaleTo100: draft.meritFormula.scaleTo100,
            tieBreakers: draft.meritFormula.tieBreakers,
          },
        },
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  // ─── Component editing ──────────────────────────────────────────────
  const addComponent = () => setDraft(d => ({
    ...d,
    meritFormula: {
      ...d.meritFormula,
      components: [...d.meritFormula.components, { source: 'test', weight: 25, normalize: true }],
    },
  }));

  const updateComponent = (idx: number, patch: Partial<MeritComponent>) => setDraft(d => ({
    ...d,
    meritFormula: {
      ...d.meritFormula,
      components: d.meritFormula.components.map((c, i) => {
        if (i !== idx) return c;
        const next = { ...c, ...patch };
        // Source changed → an old key (round / criterion) no longer makes sense.
        if (patch.source !== undefined && patch.source !== c.source) next.key = '';
        return next;
      }),
    },
  }));

  const removeComponent = (idx: number) => setDraft(d => ({
    ...d,
    meritFormula: { ...d.meritFormula, components: d.meritFormula.components.filter((_, i) => i !== idx) },
  }));

  // ─── Custom criteria ─────────────────────────────────────────────────
  const addCriterion = () => setDraft(d => ({
    ...d,
    customCriteria: [...d.customCriteria, {
      key: '',
      label: '', maxMarks: 10, enabled: true, order: d.customCriteria.length,
    }],
  }));

  const updateCriterion = (idx: number, patch: Partial<CustomCriterion>) => setDraft(d => {
    const oldKey = d.customCriteria[idx]?.key;
    const relabelNew = patch.label !== undefined && !savedCriterionKeys.has(oldKey);
    const customCriteria = assignKeys(
      d.customCriteria.map((c, i) => (i === idx ? { ...c, ...patch, ...(relabelNew ? { key: '' } : {}) } : c)),
      c => c.label, 'criterion', 40,
    );
    const newKey = customCriteria[idx].key;
    // A merit component already pointing at this new criterion follows it.
    const components = oldKey && oldKey !== newKey
      ? d.meritFormula.components.map(c => (c.source === 'custom' && c.key === oldKey ? { ...c, key: newKey } : c))
      : d.meritFormula.components;
    return { ...d, customCriteria, meritFormula: { ...d.meritFormula, components } };
  });

  const removeCriterion = (idx: number) => setDraft(d => ({
    ...d,
    customCriteria: d.customCriteria.filter((_, i) => i !== idx),
  }));

  return (
    <SectionCard
      title="Scoring & merit formula"
      description="How applicants are scored and ranked when offers are made."
      canEdit={canEdit}
      lockedReason={lockedReason}
      editing={editing}
      onEditToggle={setEditing}
      onSave={handleSave}
      onCancel={() => setDraft(withDefaults(pathway.scoringConfig))}
      saving={saving}
    >
      {empty && !editing ? (
        <EmptyState
          dense
          icon={<Calculate />}
          title="No scoring set up yet"
          description="Without a merit formula the Decisions tab can't rank applicants."
          action={canEdit && !lockedReason ? { label: 'Set up scoring', icon: <Calculate />, onClick: () => setEditing(true) } : undefined}
        />
      ) : !editing ? (
        <Stack spacing={3}>
          <FactGrid columns={4}>
            <Fact label={<HelpTip label="Scores combined as" {...FORMULA_HELP} />}>{MODE_OPTIONS.find(o => o.value === effective.meritFormula.mode)?.label}</Fact>
            <Fact label="Merit score shown">{effective.meritFormula.scaleTo100 ? 'Out of 100' : 'As calculated'}</Fact>
            <Fact label="Decimal places">{effective.meritFormula.rounding}</Fact>
            <Fact label="Weights add up to">{weightSum}</Fact>
          </FactGrid>

          <Box>
            <GroupLabel>What counts toward merit</GroupLabel>
            {effective.meritFormula.components.length === 0 ? (
              <QuietNote>Nothing yet — add the test, interview, custom criteria or previous school marks.</QuietNote>
            ) : (
              <TableCard>
                {effective.meritFormula.components.map((c, idx) => (
                  <ListRow key={idx}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500}>{SOURCE_LABEL[c.source]}</Typography>
                      {(resolveKeyLabel(c) || c.normalize) && (
                        <Typography variant="caption" color="text.secondary">
                          {[resolveKeyLabel(c), c.normalize ? 'Scaled to its maximum before weighting' : null].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={numSx}>Weight {c.weight}</Typography>
                  </ListRow>
                ))}
              </TableCard>
            )}
          </Box>

          <Box>
            <GroupLabel>Custom criteria</GroupLabel>
            {effective.customCriteria.length === 0 ? (
              <QuietNote>No custom criteria — add things like Hifz or extracurriculars to score on the applicant drawer.</QuietNote>
            ) : (
              <TableCard>
                {effective.customCriteria.map((c, idx) => (
                  <ListRow key={c.key || idx}>
                    <Typography variant="body2" fontWeight={500} color={c.enabled ? 'text.primary' : 'text.secondary'} sx={{ flex: 1, minWidth: 0 }}>
                      {c.label}
                    </Typography>
                    {!c.enabled && <ToneChip label="Off" tone="neutral" />}
                    <Typography variant="body2" color="text.secondary" sx={numSx}>{c.maxMarks} marks</Typography>
                  </ListRow>
                ))}
              </TableCard>
            )}
          </Box>

          <Box>
            <GroupLabel><HelpTip label="If two applicants tie" {...TIE_HELP} /></GroupLabel>
            {effective.meritFormula.tieBreakers.length === 0 ? (
              <QuietNote>No tie-breakers — tied applicants keep a fixed but arbitrary order.</QuietNote>
            ) : (
              <Stack spacing={0.75}>
                {effective.meritFormula.tieBreakers.map((tb, i) => (
                  <Stack key={tb} direction="row" spacing={1.5} alignItems="baseline">
                    <Typography variant="body2" color="text.disabled" sx={{ ...numSx, minWidth: 16, textAlign: 'right' }}>{i + 1}.</Typography>
                    <Typography variant="body2">{TIE_BREAKER_LABEL[tb]}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

        </Stack>
      ) : (
        <Stack spacing={2.5}>
          {/* Formula settings */}
          <Box>
            <GroupLabel><HelpTip label="Formula" {...FORMULA_HELP} /></GroupLabel>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
              <SearchableSelect
                label="Mode"
                value={effective.meritFormula.mode}
                onChange={(v) => setDraft(d => ({
                  ...d,
                  meritFormula: { ...d.meritFormula, mode: v as 'weighted-sum' | 'weighted-average' },
                }))}
                options={MODE_OPTIONS}
                clearable={false}
                minWidth={0}
                fullWidth
              />
              <TextField
                size="small"
                label="Rounding (decimals)"
                type="number"
                inputProps={{ min: 0, max: 6 }}
                value={effective.meritFormula.rounding}
                onChange={(e) => setDraft(d => ({
                  ...d,
                  meritFormula: { ...d.meritFormula, rounding: Number(e.target.value) || 0 },
                }))}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={effective.meritFormula.scaleTo100}
                    onChange={(_, v) => setDraft(d => ({
                      ...d,
                      meritFormula: { ...d.meritFormula, scaleTo100: v },
                    }))}
                  />
                }
                label="Scale to 0 – 100"
              />
              <Chip
                label={`Weights sum: ${weightSum}`}
                color={weightSum > 0 ? 'success' : 'default'}
                variant="outlined"
                sx={{ ...numSx, fontWeight: 600, alignSelf: 'center' }}
              />
            </Box>
          </Box>

          <Divider />

          {/* Components */}
          <Box>
            <GroupLabel
              action={
                <Button size="small" startIcon={<Add fontSize="small" />} onClick={addComponent}>
                  Add component
                </Button>
              }
            >
              Merit components
            </GroupLabel>
            {effective.meritFormula.components.length === 0 ? (
              <QuietNote>No components. Add Test, Interview, Custom, or Previous School % to feed the formula.</QuietNote>
            ) : (
              <Stack spacing={1}>
                {effective.meritFormula.components.map((c, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                      <SearchableSelect
                        label="Source"
                        value={c.source}
                        onChange={(v) => updateComponent(idx, { source: v as MeritComponentSource })}
                        options={SOURCE_OPTIONS}
                        clearable={false}
                        minWidth={180}
                      />
                      {c.source === 'interview' && (
                        <KeyPicker
                          label="Interview round"
                          value={c.key || ''}
                          onChange={(k) => updateComponent(idx, { key: k })}
                          options={interviewRoundOptions}
                          emptyMessage="No interview rounds configured yet. Add rounds in the Interview rounds section above."
                          allLabel="All rounds (sum)"
                        />
                      )}
                      {c.source === 'custom' && (
                        <KeyPicker
                          label="Custom criterion"
                          value={c.key || ''}
                          onChange={(k) => updateComponent(idx, { key: k })}
                          options={customCriterionOptions}
                          emptyMessage="No custom criteria yet. Add one under Custom criteria below."
                        />
                      )}
                      <TextField
                        size="small"
                        label="Weight"
                        type="number"
                        inputProps={{ min: 0, max: 100 }}
                        value={c.weight}
                        onChange={(e) => updateComponent(idx, { weight: Number(e.target.value) || 0 })}
                        sx={{ width: 120 }}
                      />
                      <FormControlLabel
                        control={<Switch size="small" checked={c.normalize} onChange={(_, v) => updateComponent(idx, { normalize: v })} />}
                        label="Normalize"
                      />
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => removeComponent(idx)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>

          {/* Custom criteria */}
          <Divider />
          <Box>
            <GroupLabel
              action={
                <Button size="small" startIcon={<Add fontSize="small" />} onClick={addCriterion}>
                  Add criterion
                </Button>
              }
            >
              Custom criteria
            </GroupLabel>
            {effective.customCriteria.length === 0 ? (
              <QuietNote>No custom criteria. Add things like "Hifz level" or "Extracurricular" to score on the applicant drawer.</QuietNote>
            ) : (
              <Stack spacing={1}>
                {effective.customCriteria.map((c, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <TextField
                        size="small"
                        label="Label"
                        value={c.label}
                        onChange={(e) => updateCriterion(idx, { label: e.target.value })}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        label="Max marks"
                        type="number"
                        inputProps={{ min: 1 }}
                        value={c.maxMarks}
                        onChange={(e) => updateCriterion(idx, { maxMarks: Number(e.target.value) || 0 })}
                        sx={{ width: 120 }}
                      />
                      <FormControlLabel
                        control={<Switch size="small" checked={c.enabled} onChange={(_, v) => updateCriterion(idx, { enabled: v })} />}
                        label="Enabled"
                      />
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => removeCriterion(idx)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>

          {/* Tie breakers */}
          <Divider />
          <Box>
            <GroupLabel><HelpTip label="Tie-breakers (in order)" {...TIE_HELP} /></GroupLabel>
            <Autocomplete
              multiple
              size="small"
              options={ALL_TIE_BREAKERS}
              getOptionLabel={(k) => TIE_BREAKER_LABEL[k]}
              value={effective.meritFormula.tieBreakers}
              onChange={(_, next) => setDraft(d => ({
                ...d, meritFormula: { ...d.meritFormula, tieBreakers: next as MeritTieBreaker[] },
              }))}
              renderInput={(params) => (
                <TextField {...params} placeholder="Add a tie-breaker…" helperText="Applied in order when two merit scores match" />
              )}
            />
          </Box>
        </Stack>
      )}

    </SectionCard>
  );
};

// ─── KeyPicker ─────────────────────────────────────────────────────────────
// Dropdown for the component.key field — options come from the cycle's
// interview rounds (for source=interview) or custom criteria (for
// source=custom). Guards against empty option lists with an inline hint.

interface KeyPickerProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  emptyMessage: string;
  /** Meaning of the empty key (e.g. "All rounds (sum)"). When set, the
   *  field can be cleared back to '' and shows this as its placeholder. */
  allLabel?: string;
}

const KeyPicker: React.FC<KeyPickerProps> = ({ label, value, onChange, options, emptyMessage, allLabel }) => {
  if (options.length === 0 && !allLabel) {
    return (
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="warning.main">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ flex: 1, minWidth: 200 }}>
      <SearchableSelect
        label={label}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={allLabel}
        clearable={!!allLabel}
        fullWidth
      />
    </Box>
  );
};

export default ScoringConfigSection;
